import {
  HostQrCodeParameters,
  PlayerStaticImageState,
  PlayerVideoState,
  QrCodeState,
  GamepadState,
  PauseState,
  PlaycastPlayerInputAccess,
} from '@playcastdotio/CoreTypes';
import { makeStateStore, makeNonSerializableStateStore } from '@playcastdotio/StateStore';
import { PlaycastWebrtcPresetGroup } from '@playcastdotio/Peer';

const initialMediaStreamState: { stream: MediaStream; audioStream: MediaStream } = {
  stream: new MediaStream(),
  audioStream: new MediaStream(),
};
export const mediaStreamState = makeNonSerializableStateStore<typeof initialMediaStreamState>(initialMediaStreamState);

export const showStaticImageState = makeStateStore<{ showStaticImage: boolean }>({
  showStaticImage: false,
});

type PlayerGamepadState = {
  playerCoordinates: {
    origin: 'topLeft' | 'bottomLeft';
    dimensions: { x: number; y: number };
    display: { x: number; y: number };
  };
  gamepads: Gamepad[];
};
const initialPlayerGamepadState: PlayerGamepadState = {
  playerCoordinates: {
    origin: 'topLeft',
    dimensions: { x: 0, y: 0 },
    display: { x: window.screen.width, y: window.screen.height },
  },
  gamepads: [],
};
export const playerGamepadState = makeNonSerializableStateStore<PlayerGamepadState>(initialPlayerGamepadState);

const initialGamepadState: GamepadState = {
  shareGamepadOne: false,
  shareGamepadTwo: false,
  shareGamepadThree: false,
  shareGamepadFour: false,
  autoAssign: true,
  maxAssignments: 4,
  playerMappings: {},
};
export const gamepadState = makeStateStore<GamepadState>(initialGamepadState);

const initialVideoState: PlayerVideoState = { enabled: true, streamId: '' };
export const playerVideoState = makeStateStore(initialVideoState);

const initialStaticImageState: PlayerStaticImageState = {
  staticImageBase64: '',
};
export const playerStaticImageState = makeStateStore(initialStaticImageState);

const initialQrCodeState: QrCodeState = {
  defaultPlayerState: {
    content: '',
    enabled: false,
    scale: 1,
    position: {
      x: 0,
      y: 0,
    },
    anchor: {
      x: 0,
      y: 0,
    },
  },
  players: {},
};
export const playerQrCodeState = makeStateStore(initialQrCodeState);

const initialHostQrCodeState: HostQrCodeParameters = {
  showTo: 'all',
  hideWhen: 'never',
};
export const hostQrCodeState = makeStateStore(initialHostQrCodeState);

const initialPauseInputstate: PauseState = {
  paused: false,
  players: {},
};
export const pauseInputState = makeStateStore(initialPauseInputstate);

const initialHostQualityPresets = {} as PlaycastWebrtcPresetGroup;
export const hostQualityPresets = makeStateStore(initialHostQualityPresets);

type PlayerConnectionState = {
  playerPeerId: string;
  peerToken?: string;
  signalQuality?: 'ideal' | 'degraded' | 'worst' | 'none';
  broadcastSessionId: string;
  status: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'refused' | 'reconnecting';
  kickReason?: string;
  iceServers?: {
    urls: string[];
    username: string;
    credential: string;
  };
  allowPeerConnections: boolean;
};

export const playerConnectionState = makeStateStore<PlayerConnectionState>({
  status: 'idle',
  playerPeerId: '',
  broadcastSessionId: '',
  allowPeerConnections: true,
});

type PlayerInfoState = {
  players: Record<
    string,
    {
      access: PlaycastPlayerInputAccess;
      gamepadSlots: number[];
      displayName: string;
    }
  >;
};

const initialPlayerInfoState: PlayerInfoState = {
  players: {},
};
export const playerInfoState = makeStateStore<PlayerInfoState>(initialPlayerInfoState);

const initialTruncMachineIdState = '';
export const truncMachineIdState = makeStateStore<string>(initialTruncMachineIdState);

// Debug "a macro is running" flag received from the host over the player
// replication channel (channel name must match the host store export). Display
// is gated on debug mode in the player indicator component.
export const macroRunningState = makeStateStore<{ macroRunning: boolean }>({ macroRunning: false });
